import { Injectable, NgZone, inject, signal } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker'
import { filter } from 'rxjs'

const CHECK_EVERY_MS = 10 * 60 * 1000
/** A new build found this soon after opening (before any interaction) is applied without asking. */
const FRESH_OPEN_MS = 20 * 1000
/** Coming back after this long counts as opening the dashboard again. */
const AWAY_MS = 30 * 60 * 1000

/**
 * Keeps the installed app on the latest deploy.
 *
 * ngsw serves the cached build first (that's what makes the app open offline
 * and instantly), so a new deploy is downloaded in the background. Every time
 * the dashboard is opened — a fresh load, or the tab coming back after being
 * away for a while — it checks for a new build at once, and if one is ready
 * before the merchant starts working it is activated and the page reloaded.
 *
 * Mid-session the merchant is offered a one-click reload instead (never
 * yanking a half-filled form), and the update is also applied on the next
 * route change, after route guards have allowed leaving the page.
 *
 * Checks also run when the connection comes back and every 10 minutes. A
 * broken cache (unrecoverable state) reloads straight away.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private sw = inject(SwUpdate)
  private router = inject(Router)
  private zone = inject(NgZone)

  readonly updateReady = signal(false)
  private started = false
  /** True while the dashboard counts as "just opened": updates apply silently. */
  private freshOpen = true
  private freshTimer?: ReturnType<typeof setTimeout>
  private hiddenAt = 0
  private applying = false

  start(): void {
    if (this.started || !this.sw.isEnabled) return
    this.started = true

    this.sw.versionUpdates.pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY')).subscribe(() => {
      if (this.freshOpen) void this.applyUpdate()
      else this.updateReady.set(true)
    })
    this.sw.unrecoverable.subscribe(() => this.reloadPage())

    // Route change = the merchant already left the page (guards passed): load the new build there.
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
      if (this.updateReady()) void this.applyUpdate()
    })

    this.openWindow()
    this.check()

    // Any interaction ends the silent window: from then on, ask instead of reloading.
    const endFresh = () => this.endFresh()
    for (const type of ['pointerdown', 'keydown'] as const) window.addEventListener(type, endFresh, { passive: true })

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.hiddenAt = Date.now()
        return
      }
      if (this.hiddenAt && Date.now() - this.hiddenAt >= AWAY_MS) {
        // Back after a long time: same as opening the dashboard again.
        if (this.updateReady()) return void this.applyUpdate()
        this.openWindow()
      }
      this.check()
    })
    window.addEventListener('online', () => this.check())
    // Outside Angular so the interval never keeps the app from becoming stable.
    this.zone.runOutsideAngular(() => setInterval(() => this.check(), CHECK_EVERY_MS))
  }

  async applyUpdate(): Promise<void> {
    if (this.applying) return
    this.applying = true
    try {
      await this.sw.activateUpdate()
    } finally {
      this.reloadPage()
    }
  }

  /** Full reload so the new build's index.html and bundles load (overridable in tests). */
  reloadPage(): void {
    location.reload()
  }

  private check(): void {
    this.sw.checkForUpdate().catch(() => undefined)
  }

  private openWindow(): void {
    this.freshOpen = true
    clearTimeout(this.freshTimer)
    this.freshTimer = setTimeout(() => this.endFresh(), FRESH_OPEN_MS)
  }

  private endFresh(): void {
    this.freshOpen = false
    clearTimeout(this.freshTimer)
  }
}
